import React from "react"

import { motion } from "framer-motion"
import { NotificationManager } from "react-notifications"

import { ArrowDownIcon, ArrowUpIcon } from "@heroicons/react/24/solid"

import classNames from "src/classNames"
import { RunMutation } from "src/types"

import { gql, useMutation } from "hooks/urql"

import ExpandableImageCollection from "components/ExpandableImageCollection"
import EditableDisplay from "components/Forms/EditableDisplay"
import RemoveButton from "components/RemoveButton"

import lessonResourcesFragment from "./lessonResourcesFragment"

const LessonResource = ({ resource, first, last }) => {
  const [, updateLessonResource] = useMutation(
    updateLessonResourceMutation
  ) as [any, RunMutation]
  const [{ fetching: incrementOrderRunning }, incrementOrder] = useMutation(
    incrementLessonResourceOrderMutation
  ) as [{ fetching: boolean }, RunMutation]
  const [{ fetching: decrementOrderRunning }, decrementOrder] = useMutation(
    decrementLessonResourceOrderMutation
  ) as [{ fetching: boolean }, RunMutation]

  const incrementOrderHandler = () => {
    incrementOrder({ id: resource.id }).then(result => {
      if (result.error) NotificationManager.error(result.error.message)
    })
  }

  const decrementOrderHandler = () =>
    decrementOrder({ id: resource.id }).then(result => {
      if (result.error) NotificationManager.error(result.error.message)
    })

  const updateLessonResourceHandler = ({ value }) =>
    updateLessonResource({ id: resource.id, name: value }).then(result => {
      if (result.error) NotificationManager.error(result.error.message)
    })

  const ResourceName = ({ children }) => {
    const [, removeLessonResource] = useMutation(
      removeLessonResourceMutation
    ) as [any, RunMutation]
    const removeLessonResourceHandler = () =>
      removeLessonResource({ id: resource.id }).then(result => {
        if (result.error) NotificationManager.error(result.error.message)
      })

    return (
      <div className="flex items-center">
        <RemoveButton onClick={removeLessonResourceHandler} />
        <strong>{children}</strong>
      </div>
    )
  }

  return (
    <motion.li positionTransition className="mb-3 flex justify-between">
      <div className="ml-3 flex gap-4">
        <div className="w-72">
          <EditableDisplay
            displayValue={resource.name}
            save={updateLessonResourceHandler}
            tag={ResourceName}
          />
        </div>
        <div className="mt-2">
          <button
            className={classNames(
              "cursor-pointer rounded-md border-none bg-inherit pt-2 text-xl hover:bg-gray-200",
              (last || incrementOrderRunning) && "cursor-not-allowed"
            )}
            onClick={incrementOrderHandler}
            disabled={last || incrementOrderRunning}
          >
            <ArrowDownIcon className="h-6 w-6" />
          </button>
          <button
            className={classNames(
              "cursor-pointer rounded-md border-none bg-inherit pt-2 text-xl hover:bg-gray-200",
              (first || decrementOrderRunning) && "cursor-not-allowed"
            )}
            onClick={decrementOrderHandler}
            disabled={first || decrementOrderRunning}
          >
            <ArrowUpIcon className="h-6 w-6" />
          </button>
        </div>
      </div>
      <ExpandableImageCollection images={[resource.file]} />
    </motion.li>
  )
}

const updateLessonResourceMutation = gql`
  mutation UpdateLessonResourceMutation($id: ID!, $name: String!) {
    updateLessonResource(id: $id, name: $name) {
      lessonResource {
        id
        name
      }
    }
  }
`

const decrementLessonResourceOrderMutation = gql`
  ${lessonResourcesFragment}
  mutation DecrementLessonResourceOrderMutation($id: ID!) {
    decrementLessonResourceOrder(id: $id) {
      lessonTopic {
        id
        lessonResources {
          ...LessonResourcesFields
        }
      }
    }
  }
`

const incrementLessonResourceOrderMutation = gql`
  ${lessonResourcesFragment}
  mutation IncrementLessonResourceOrderMutation($id: ID!) {
    incrementLessonResourceOrder(id: $id) {
      lessonTopic {
        id
        lessonResources {
          ...LessonResourcesFields
        }
      }
    }
  }
`

const removeLessonResourceMutation = gql`
  ${lessonResourcesFragment}
  mutation RemoveLessonResourceMutation($id: ID!) {
    removeLessonResource(id: $id) {
      lessonTopic {
        id
        lessonResources {
          ...LessonResourcesFields
        }
      }
    }
  }
`

export default LessonResource
